import { useState } from 'react';
import { saveAs } from 'file-saver';
import { useRelayEnvironment } from 'react-relay';
import type { IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useCoreDataFilter, fetchCoreData } from '../../../../services/core-data/main.tsx';
import { useProject } from '../../../../services/get-project';
import { buildCsv } from './build-csv';
import { buildFilename } from './build-filename';

const maximumIssuePages = 10;

const useIssueUrlBuilder = (): ((arg1: IssueKey) => string) => {
	const { hostname } = window.location;
	return (key: IssueKey) => `https://${hostname}/browse/${key}`;
};

type Exporter =
	| {
			status: 'initializing';
	  }
	| {
			status: 'ready';
			beginExport: () => void;
	  }
	| {
			status: 'exporting';
	  }
	| {
			status: 'failed';
			error: Error;
			beginExport: () => void;
	  };

export const useCsvExporter = (): Exporter => {
	const [isExporting, setIsExporting] = useState(false);
	const [error, setError] = useState<unknown>(null);
	const buildUrl = useIssueUrlBuilder();
	const cloudId = useCloudId();
	const { data: project } = useProject();
	const filter = useCoreDataFilter();
	const environment = useRelayEnvironment();

	if (isExporting) {
		return { status: 'exporting' };
	}

	if (filter === null || project === undefined) {
		return { status: 'initializing' };
	}

	const beginExport = async () => {
		try {
			setIsExporting(true);
			setError(null);

			const result = await fetchCoreData(
				environment,
				filter,
				maximumIssuePages,
				project.id,
				cloudId,
			);

			if (result === null) {
				throw Error('Error fetching data for CSV export');
			}

			const { allDeployments, issuesByIssueId } = result.coreData;
			const csv = buildCsv(allDeployments, issuesByIssueId, buildUrl);
			const filename = buildFilename(project.key, new Date());
			const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });

			saveAs(blob, filename);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (e: any) {
			setError(e);
		} finally {
			setIsExporting(false);
		}
	};

	if (error) {
		return {
			status: 'failed',
			// @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'Error'.
			error,
			beginExport: () => {
				beginExport();
			},
		};
	}

	return {
		status: 'ready',
		beginExport: () => {
			beginExport();
		},
	};
};
